import React, { useState } from "react";

interface ValidationError {
  row: number;
  field: string;
  message: string;
  value?: any;
}

interface BulkUploadValidationProps {
  data: any[];
  errors: ValidationError[];
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export const BulkUploadValidation: React.FC<BulkUploadValidationProps> = ({
  data,
  errors,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const hasErrors = errors.length > 0;
  const totalRows = data.length;
  const validRows = totalRows - errors.length;

  // Calcular paginación
  const totalPages = Math.ceil(totalRows / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Validación de Datos
        </h3>
        <p className="text-gray-600">
          Revisa los datos antes de proceder con la carga masiva
        </p>
      </div>

      {/* Resumen */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-900 mb-2">Resumen:</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Total de filas:</span> {totalRows}
          </div>
          <div>
            <span className="font-medium">Filas válidas:</span>{" "}
            <span className="text-green-600">{validRows}</span>
          </div>
          <div>
            <span className="font-medium">Errores encontrados:</span>{" "}
            <span className={hasErrors ? "text-red-600" : "text-green-600"}>
              {errors.length}
            </span>
          </div>
          <div>
            <span className="font-medium">Estado:</span>{" "}
            <span className={hasErrors ? "text-yellow-600" : "text-green-600"}>
              {hasErrors ? "Requiere atención" : "Listo para cargar"}
            </span>
          </div>
        </div>
      </div>

      {/* Vista previa de datos */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium text-gray-900">Vista previa de datos:</h4>
          {totalPages > 1 && (
            <div className="text-sm text-gray-600">
              Página {currentPage} de {totalPages}({startIndex + 1}-
              {Math.min(endIndex, totalRows)} de {totalRows} filas)
            </div>
          )}
        </div>

        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Fila
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Año
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Unidad
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Tema
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Objetivos de Aprendizaje
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Explicación
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Ejemplo
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Resultado
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Dificultad
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Conceptos Previos
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  Tiene Imagen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentData.map((row, index) => {
                const actualRowIndex = startIndex + index;
                const rowErrors = errors.filter(
                  (e) => e.row === actualRowIndex + 1
                );
                const hasRowError = rowErrors.length > 0;

                return (
                  <tr
                    key={actualRowIndex}
                    className={hasRowError ? "bg-red-50" : ""}
                  >
                    <td className="px-3 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                      {actualRowIndex + 1}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                      {row.anio || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                      {row.unidad || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                      {row.tema || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b max-w-xs truncate">
                      {row.objetivosAprendizaje || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b max-w-xs truncate">
                      {row.explicacion || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b max-w-xs truncate">
                      {row.ejemplo || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                      {row.resultado || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                      {row.dificultad || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b max-w-xs truncate">
                      {row.conceptosPrevios || "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                      {(() => {
                        if (typeof row.tieneImagen === "boolean") {
                          return row.tieneImagen ? "Sí" : "No";
                        }
                        if (typeof row.tieneImagen === "string") {
                          const valor = row.tieneImagen.toLowerCase().trim();
                          return valor === "si" ||
                            valor === "sí" ||
                            valor === "true" ||
                            valor === "yes" ||
                            valor === "1"
                            ? "Sí"
                            : "No";
                        }
                        if (typeof row.tieneImagen === "number") {
                          return row.tieneImagen === 1 ? "Sí" : "No";
                        }
                        return "No";
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-4">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === pageNum
                      ? "bg-blue-500 text-white border-blue-500"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Errores */}
      {hasErrors && (
        <div className="mb-6">
          <h4 className="font-medium text-red-900 mb-3">
            Errores encontrados ({errors.length}):
          </h4>
          <div className="bg-red-50 rounded-lg p-4 max-h-60 overflow-y-auto">
            <ul className="space-y-2">
              {errors.map((error, index) => (
                <li key={index} className="text-sm text-red-800">
                  <span className="font-medium">Fila {error.row}:</span>{" "}
                  <span className="font-medium">{error.field}</span> -{" "}
                  {error.message}
                  {error.value && (
                    <span className="text-red-600">
                      {" "}
                      (Valor: "{error.value}")
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className={`px-6 py-2 rounded-lg text-white font-medium disabled:opacity-50 ${
            hasErrors
              ? "bg-yellow-500 hover:bg-yellow-600"
              : "bg-green-500 hover:bg-green-600"
          }`}
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Procesando...</span>
            </div>
          ) : hasErrors ? (
            "Cargar con errores"
          ) : (
            "Cargar datos"
          )}
        </button>
      </div>

      {/* Advertencia si hay errores */}
      {hasErrors && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                Se encontraron errores en algunos datos. Puedes proceder con la
                carga, pero las filas con errores serán omitidas.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
